import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import type {
  BulkUserOperationResult,
  BulkInviteOperationFunc,
  CustomerUserMeta,
  UserProfileInfo,
  UserProfileParam,
  BulkRevokeOperationFunc,
  UserOperationResult,
  BulkEditOperationFunc,
} from './types';
import instance from '@src/api/axiosInstance';
import { useSetAtom, useAtomValue } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import {
  asyncLoadingMaskAtom,
  panelVisibleAtom,
  toInviteUserAtom,
  usersToRevokeAtom,
  userToEditAtom,
  usersToBulkEditAtom,
} from './store';
import { useCallback } from 'react';
import { message } from 'antd';

export const useUpdateEditorState = () => {
  const resetUserInvite = useResetAtom(toInviteUserAtom);
  const resetUsersRevoke = useResetAtom(usersToRevokeAtom);
  const resetUserEdit = useResetAtom(userToEditAtom);
  const resetUsersBulkEdit = useResetAtom(usersToBulkEditAtom);

  const reset = () => {
    resetUserInvite();
    resetUserEdit();
    resetUsersRevoke();
    resetUsersBulkEdit();
  };

  return {
    reset,
    resetUserInvite,
    resetUserEdit,
    resetUsersRevoke,
    resetUsersBulkEdit,
  };
};

export const useGetEditorVisible = () => {
  const userToEdit = Boolean(useAtomValue(userToEditAtom));
  const usersToRevoke = useAtomValue(usersToRevokeAtom);
  const toInviteUser = Boolean(useAtomValue(toInviteUserAtom));
  const usersToBulkEdit = useAtomValue(usersToBulkEditAtom);
  const isRevokeUsersVisible = Boolean(usersToRevoke);
  const isBulkEditVisible = Boolean(usersToBulkEdit);
  return {
    isVisible: userToEdit || isRevokeUsersVisible || toInviteUser || isBulkEditVisible,
    isEditUserVisible: userToEdit,
    isInviteUserVisible: toInviteUser,
    isRevokeUsersVisible,
    isBulkEditVisible,
  };
};

export function useClosePanel() {
  const setVisible = useSetAtom(panelVisibleAtom);
  const setAsyncLoadingMask = useSetAtom(asyncLoadingMaskAtom);
  const { reset } = useUpdateEditorState();
  const handleClosePanel = useCallback(() => {
    setVisible(false);
    setAsyncLoadingMask(false);
    reset();
  }, [reset, setAsyncLoadingMask, setVisible]);
  return handleClosePanel;
}

/**
 * Server-side user-list filters (map ticket T8/T9). Sent as query params to GET /users; the server
 * filters + returns the full matching set (the client still paginates). A SINGLE permission filter
 * only (the server 400s on more) — the table enforces that in the UI.
 */
export interface UserListFilters {
  search?: string;
  roleId?: number;
  permKey?: string;
  /** 'true'/'false' for booleans, an enum value string, or 'null' for a no-access enum option */
  permValue?: string;
}

function buildUserListQuery(filters?: UserListFilters): string {
  const params = new URLSearchParams();
  if (filters?.search) {
    params.set('search', filters.search);
  }
  if (filters?.roleId !== undefined) {
    params.set('role', String(filters.roleId));
  }
  if (filters?.permKey && filters.permValue !== undefined) {
    params.set('permKey', filters.permKey);
    params.set('permValue', filters.permValue);
  }
  const qs = params.toString();
  return qs ? `/users?${qs}` : '/users';
}

export const useGetUserList = (
  options: UseQueryOptions<UserProfileInfo[], unknown, UserProfileInfo[], string[]> & {
    filters?: UserListFilters;
  },
) => {
  const { filters } = options;
  return useQuery(
    // Key by the filters so the server is re-queried whenever they change (server-side filtering).
    ['get-user-list', JSON.stringify(filters ?? {})],
    async () => {
      const res = await instance.get<UserProfileInfo[]>(buildUserListQuery(filters));
      return res.data ?? [];
    },
    {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      // The list is unmounted while the bulk/edit panel is open, so an invalidate fired during a
      // mutation has no active observer to refetch. Refetch on (re)mount so returning to the list
      // after a bulk edit / invite / revoke always shows fresh data (map ticket T8/T9).
      refetchOnMount: 'always',
      enabled: options.enabled,
    },
  );
};

export const useGetUserMeta = () => {
  return useQuery(
    ['get-user-meta'],
    async () => {
      const res = await instance.get<CustomerUserMeta>('/users/meta');
      return res.data ?? {};
    },
    {
      // 10min
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
};

export const requestInviteUsers: BulkInviteOperationFunc = async (info) => {
  const res = await instance.post<BulkUserOperationResult>('/users', info);
  return res?.data ?? [];
};

export const requestEditUser = async (param: { userId: number; props: UserProfileParam }) => {
  const { userId, props } = param;
  const res = await instance.put<UserOperationResult>('/users/' + userId, props);
  return res.data ?? {};
};

export const requestRevokeUsers: BulkRevokeOperationFunc = async (info) => {
  const res = await instance.request<BulkUserOperationResult>({
    method: 'delete',
    url: '/users',
    data: info,
  });
  return res.data ?? [];
};

export const requestBulkEditPermissions: BulkEditOperationFunc = async (info) => {
  const res = await instance.post('/users/bulk-permissions', info);
  return res.data ?? undefined;
};

export function useInviteUsers() {
  const queryClient = useQueryClient();

  const mutation = useMutation(requestInviteUsers, {
    onSuccess: () => {
      queryClient.invalidateQueries(['get-user-list']);
    },
    onError: () => {
      message.error('There was an error while inviting users. Please try again later.');
    },
  });

  return mutation;
}

export function useRevokeUsers() {
  const queryClient = useQueryClient();

  const mutation = useMutation(requestRevokeUsers, {
    onSuccess: () => {
      queryClient.invalidateQueries(['get-user-list']);
    },
    onError: () => {
      message.error('There was an error while revoking users. Please try again later.');
    },
  });

  return mutation;
}

export function useEditUser() {
  const queryClient = useQueryClient();

  const mutation = useMutation(requestEditUser, {
    onSuccess: () => {
      queryClient.invalidateQueries(['get-user-list']);
    },
    onError: () => {
      message.error('There was an error while editing user. Please try again later.');
    },
  });

  return mutation;
}

export function useBulkEditPermissions() {
  const queryClient = useQueryClient();

  const mutation = useMutation(requestBulkEditPermissions, {
    onSuccess: () => {
      // Bulk edit runs in ModalLayer, which REPLACES the UserList (Main renders
      // `{!isPanelVisible && <UserList/>}`), so the list is unmounted while the panel is open.
      // Just mark every cached list entry stale here; the freshness guarantee on return comes from
      // `useGetUserList`'s `refetchOnMount: 'always'`, which unconditionally refetches the active
      // filter combo when UserList remounts. Using `refetchQueries` instead would eagerly re-query
      // EVERY cached filter/page combo immediately (server load for pages the operator may never
      // revisit) and is redundant with the remount refetch for the one combo that's actually shown.
      queryClient.invalidateQueries(['get-user-list']);
    },
    onError: () => {
      message.error('There was an error while updating permissions. Please try again later.');
    },
  });

  return mutation;
}
